import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Home, Lock, Unlock, Clock, MapPin, User, Loader2 } from 'lucide-react';

export default function SettingsModal({
  open, onOpenChange,
  startLocation, setStartLocation,
  startPostcode, setStartPostcode, startLoading, onSetStart,
  fixEndPoint, setFixEndPoint,
  startTime, setStartTime,
  timeAtStop, setTimeAtStop,
  driverLocation,
  setRoute,
  onSave,
}) {
  const [startLocked, setStartLocked] = useState(!!startLocation);

  const handleToggleLock = async () => {
    if (!startLocked) {
      // Locking — if there's a new postcode typed, set it first
      if (startPostcode.trim()) {
        await onSetStart();
      }
      setStartLocked(true);
    } else {
      // Unlocking — allow editing
      if (startLocation) setStartPostcode(startLocation.postcode || '');
      setStartLocked(false);
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter' && startPostcode.trim()) {
      await onSetStart();
      setStartLocked(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o) setStartLocked(!!startLocation);
      onOpenChange(o);
    }}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Route Settings</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">

          {/* Start Location with Lock/Unlock */}
          <div>
            <Label className="text-base font-medium mb-2 block"><Home className="w-4 h-4 inline mr-2" />Start Location</Label>
            <div className="flex items-center gap-2">
              {startLocked && startLocation ? (
                <div className="flex-1 flex items-center gap-3 p-3 bg-[#007aff]/5 border border-[#007aff]/20 rounded-xl">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-bold text-[#1d1d1f]">{startLocation.postcode}</div>
                    <div className="text-xs text-[#86868b] truncate">{startLocation.address}</div>
                  </div>
                </div>
              ) : (
                <Input
                  placeholder="Enter postcode..."
                  value={startPostcode}
                  onChange={(e) => setStartPostcode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 h-12"
                  data-testid="start-postcode-input"
                  autoFocus={!startLocked}
                />
              )}
              <Button
                onClick={handleToggleLock}
                disabled={startLoading || (!startLocked && !startPostcode.trim() && !startLocation)}
                className={`h-12 w-12 flex-shrink-0 rounded-xl transition-all duration-200 ${
                  startLocked
                    ? 'bg-[#007aff] hover:bg-[#0066d6] text-white'
                    : 'bg-[#f5f5f7] hover:bg-[#e8e8ed] text-[#86868b]'
                }`}
                data-testid="start-location-lock-btn"
              >
                {startLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : startLocked ? (
                  <Lock className="w-5 h-5" />
                ) : (
                  <Unlock className="w-5 h-5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-[#86868b] mt-1.5">
              {startLocked ? 'Locked — tap the lock to change' : 'Enter a postcode and tap the lock to set'}
            </p>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-xl">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-muted-foreground" />
              <div>
                <Label htmlFor="fix-end-settings" className="text-base font-medium cursor-pointer">End at Last Stop</Label>
                <p className="text-xs text-muted-foreground">Keep the last destination fixed when optimizing</p>
              </div>
            </div>
            <Switch id="fix-end-settings" checked={fixEndPoint} onCheckedChange={setFixEndPoint} />
          </div>

          <div>
            <Label className="text-base font-medium mb-2 block"><Clock className="w-4 h-4 inline mr-2" />Start Time</Label>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="h-12 font-mono text-lg" data-testid="start-time-input" />
            <p className="text-xs text-muted-foreground mt-1">ETAs will be calculated from this time</p>
          </div>

          <div>
            <Label className="text-base font-medium mb-2 block"><MapPin className="w-4 h-4 inline mr-2" />Time at Each Stop</Label>
            <div className="flex items-center gap-3">
              <Input type="number" min="0" max="60" value={timeAtStop} onChange={(e) => setTimeAtStop(Math.max(0, parseInt(e.target.value) || 0))} className="h-12 font-mono text-lg w-24" data-testid="time-at-stop-input" />
              <span className="text-muted-foreground">minutes</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Average time spent at each delivery stop</p>
          </div>

          {driverLocation && (
            <div>
              <Label className="text-base font-medium mb-2 block"><User className="w-4 h-4 inline mr-2" />Driver Location</Label>
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
                <div className="text-sm font-mono">{driverLocation.lat.toFixed(5)}, {driverLocation.lng.toFixed(5)}</div>
                <div className="text-xs text-muted-foreground mt-1">Last updated: {new Date(driverLocation.updated_at).toLocaleTimeString()}</div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => { onSave(); onOpenChange(false); }} className="w-full h-12">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
